import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './styles/ServicesPage.css';
import '../../components/Services/styles/ServiceForm.css';
import showToast from '../../utils/toast';
import ServicesTable, {
  SERVICES_TABLE_FILTERS_STORAGE_KEY,
  ServicesTableFilters,
  createDefaultServicesTableFilters,
} from '../../components/Services/tables/ServicesTable';
import { readSessionStorageValue } from '../../hooks/useSessionStorageState';
import useServicesService from '../../services/useServicesService';
import ServiceCreationWizard from '../../components/Services/wizard/ServiceCreationWizard';
import { waitMinServiceCreationLoading } from '../../components/Services/wizard/ServiceCreationLoadingScreen';
import { isStepValidByNumber } from '../../components/Services/wizard/serviceWizardValidation';
import { Service } from '../../models/ServiceModel';
import useCompaniesService from '../../services/useCompaniesService';
import { useNavigate } from 'react-router-dom';
import { ServicePriority, ServiceStatus, ServiceType } from '../../enums/ServiceEnum';
import { subscribeSocket } from '../../services/socketService';
import { useFormErrors } from '../../hooks/useFormErrors';
import Pagination, { MAX_PAGE_SIZE } from '../../components/Base/Pagination';
import ServiceDuplicateModal from '../../components/Services/forms/ServiceDuplicateModal';
import ServiceReturnModal from '../../components/Services/forms/ServiceReturnModal';
import ServicesPlanillaExportModal from '../../components/Services/forms/ServicesPlanillaExportModal';
import ElAbraExcelImportModal from '../../components/Services/forms/ElAbraExcelImportModal';
import ReleaseDriverModal from '../../components/Services/ReleaseDriverModal';
import ServiceRouteEditModal from '../../components/Services/forms/ServiceRouteEditModal';
import {
  getMinServiceDateYmd,
  isServiceDateTimeInPast,
} from '../../utils/serviceScheduleValidation';
import { applyGeneralModeDateDefaults } from '../../utils/serviceGeneralModeDefaults';
import { isAdicionalEnabledForCompany } from '../../config/adicionalCompany';
import {
  canExportEmailsAndServicesExcel,
  canImportElAbraExcel,
} from '../../hooks/usePermissions';

const createEmptyServiceForm = (): Service => ({
  id: undefined,
  company: 0,
  service_time: '',
  service_date: '',
  description: '',
  origin: null,
  destination: null,
  driver: null,
  authorizer: null,
  cost_center: null,
  vehicle: null,
  sequences: [],
  selected_passenger: null,
  selected_address: null,
  service_type: ServiceType.MANUAL,
  priority: ServicePriority.NORMAL,
  style_color: null,
  enaex_code: '',
  status: ServiceStatus.PREPARACION,
  el_abra_group: null,
  management: null,
  superintendency: null,
  area: null,
  service_reason_text: '',
});

const formatPassengerFilterLabel = (p: {
  name?: string | null;
  first_lastname?: string | null;
  second_lastname?: string | null;
}) =>
  [p.name, p.first_lastname, p.second_lastname]
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(" ") || "Sin nombre";

/**
 * Filtros que viajan a la API (incluye salidas próximas).
 */
const mapTableFiltersToPageFilters = (t: ServicesTableFilters) => ({
  id: t.id,
  fromDate: t.fechaDesde,
  toDate: t.fechaHasta,
  fromTime: t.horaDesde,
  toTime: t.horaHasta,
  company: t.empresa,
  status: t.estado,
  origen: t.origen,
  destino: t.destino,
  costCenter: t.centroCosto,
  enaexCode: t.enaexCode,
  passengers: t.pasajeros,
  proximasHoras: t.proximasHoras,
  /** false = carga diaria (ventana en servidor); true = búsqueda masiva sin ventana */
  generalMode: t.generalMode,
  /** true = últimos servicios agregados (orden por creación, sin ventana) */
  recentMode: t.recentMode,
});

const ServicesPage: React.FC = () => {
  const navigate = useNavigate();
  const svc = useServicesService();
  const companiesSvc = useCompaniesService();
  const { list: listCompanies } = companiesSvc;
  const { list, store, updateStatus, assignDriver, releaseDriver, step3, updateAdicional, fetching, step2, filterPassengers: loadFilterPassengerOptions } = svc;

  const [services, setServices] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false,
  });
  // Hidratar desde sessionStorage para que la carga inicial respete los filtros
  // persistidos por la tabla (se mantienen al retroceder desde otra página)
  const [filters, setFilters] = useState(() =>
    mapTableFiltersToPageFilters(
      applyGeneralModeDateDefaults(
        readSessionStorageValue(
          SERVICES_TABLE_FILTERS_STORAGE_KEY,
          createDefaultServicesTableFilters()
        )
      )
    )
  );
  const [filterPassengers, setFilterPassengers] = useState<
    Array<{ id: number; label: string; company_id?: number | null }>
  >([]);
  const [showForm, setShowForm] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<Service | null>(null);
  const [retornoTarget, setRetornoTarget] = useState<Service | null>(null);
  const [showPlanillaExport, setShowPlanillaExport] = useState(false);
  const [showElAbraImport, setShowElAbraImport] = useState(false);
  const canImportElAbraPlanilla = canImportElAbraExcel();
  const canExportPlanilla = canExportEmailsAndServicesExcel();
  const [step, setStep] = useState(1);
  const [savingLocked, setSavingLocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusTransitionLoadingIds, setStatusTransitionLoadingIds] = useState<number[]>([]);
  const [assignDriverLoadingIds, setAssignDriverLoadingIds] = useState<number[]>([]);
  const [releaseDriverLoadingIds, setReleaseDriverLoadingIds] = useState<number[]>([]);
  const [releaseDriverTarget, setReleaseDriverTarget] = useState<Service | null>(null);
  const [routeEditTarget, setRouteEditTarget] = useState<Service | null>(null);
  const [driverOptionsByCompany, setDriverOptionsByCompany] = useState<
    Record<number, Array<{ value: number; label: string; is_available?: boolean }>>
  >({});
  const [driversLoadingCompanyIds, setDriversLoadingCompanyIds] = useState<number[]>([]);
  const [form, setForm] = useState<Service>(createEmptyServiceForm);


  const [companies, setCompanies] = useState<{ value: string; label: string }[]>([]);

  const companyName = useMemo(
    () => companies.find((c) => String(c.value) === String(form.company))?.label ?? null,
    [companies, form.company]
  );
  const [passengers, setPassengers] = useState<any[]>([]);
  const [locations, setLocations] = useState<{ value: string; label: string }[]>([]); // 
  const [drivers, setDrivers] = useState<any[]>([]);
  const [authorizers, setAuthorizers] = useState<any[]>([]);
  const [requesters, setRequesters] = useState<any[]>([]);
  const [costCenters, setCostCenters] = useState<any[]>([]);
  const activeRequest = useRef<AbortController | null>(null);
  const filterPassengersRequestRef = useRef<AbortController | null>(null);
  const loadFilterPassengerOptionsRef = useRef(loadFilterPassengerOptions);
  const paginationRef = useRef(pagination);
  const filtersRef = useRef(filters);

  const passengerFilterOptions = useMemo(() => {
    const companyId = filters.company ? Number(filters.company) : null;
    const base =
      companyId && !Number.isNaN(companyId)
        ? filterPassengers.filter((p) => Number(p.company_id) === companyId)
        : filterPassengers;

    return base.map((p) => ({
      value: String(p.id),
      label: p.label,
    }));
  }, [filterPassengers, filters.company]);

  // Mantener referencia actualizada de paginación
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();

  const [isStepValid, setIsStepValid] = useState(false);

  const isServiceDateValid = useCallback((dateRaw?: string) => {
    const date = String(dateRaw ?? "").trim();
    if (!date) return false;
    return date >= getMinServiceDateYmd();
  }, []);

  const mergePassengersFromServices = useCallback((items: any[]) => {
    setFilterPassengers((prev) => {
      const byId = new Map(prev.map((p) => [p.id, p]));
      let changed = false;

      for (const service of items) {
        const details = Array.isArray(service?.passenger_details)
          ? service.passenger_details
          : [];
        for (const detail of details) {
          const id = Number(detail?.id);
          if (!id || Number.isNaN(id) || byId.has(id)) continue;
          changed = true;
          byId.set(id, {
            id,
            label: formatPassengerFilterLabel({ name: detail?.name }),
            company_id: service?.company ?? null,
          });
        }
      }

      if (!changed) return prev;

      return Array.from(byId.values()).sort((a, b) =>
        a.label.localeCompare(b.label, "es", { sensitivity: "base" })
      );
    });
  }, []);


  // ✅ cargar servicios existentes con filtros
  const loadServices = useCallback((
    page: number = 1,
    currentFilters = filtersRef.current,
    pageSizeParam?: number
  ) => {
    setLoadingServices(true);
    activeRequest.current?.abort();
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    const requestedPageSize = Math.min(
      MAX_PAGE_SIZE,
      pageSizeParam !== undefined
        ? pageSizeParam
        : paginationRef.current.pageSize
    );

    if (pageSizeParam !== undefined) {
      paginationRef.current = {
        ...paginationRef.current,
        page: 1,
        pageSize: requestedPageSize,
      };
      setPagination((prev) => ({
        ...prev,
        page: 1,
        pageSize: requestedPageSize,
      }));
    }

    // Construir objeto de filtros para la API
    const apiFilters: any = { page, page_size: requestedPageSize };

    if (currentFilters.id && currentFilters.id.trim()) {
      apiFilters.id = currentFilters.id.trim();
    }
    if (currentFilters.fromDate) {
      apiFilters.start_date = currentFilters.fromDate;
    }
    if (currentFilters.toDate) {
      apiFilters.end_date = currentFilters.toDate;
    }
    if (currentFilters.fromTime) {
      apiFilters.start_time = currentFilters.fromTime;
    }
    if (currentFilters.toTime) {
      apiFilters.end_time = currentFilters.toTime;
    }
    if (currentFilters.company) {
      apiFilters.company_id = currentFilters.company;
    }
    if (currentFilters.enaexCode.trim()) {
      apiFilters.enaex_code = currentFilters.enaexCode.trim();
    }
    if (currentFilters.status) {
      apiFilters.status = currentFilters.status;
    }
    if (currentFilters.origen) {
      apiFilters.origin_id = currentFilters.origen;
    }
    if (currentFilters.destino) {
      apiFilters.destination_id = currentFilters.destino;
    }
    if (currentFilters.costCenter) {
      apiFilters.cost_center_id = currentFilters.costCenter;
    }
    if (currentFilters.passengers.length > 0) {
      apiFilters.passenger_ids = currentFilters.passengers.join(",");
    }
    if (currentFilters.generalMode) {
      apiFilters.general_mode = "true";
    }
    if (currentFilters.recentMode) {
      apiFilters.recent_mode = "true";
    }
    if (currentFilters.proximasHoras) {
      apiFilters.proximas_horas = currentFilters.proximasHoras;
    }

    list(apiFilters, {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        const items = Array.isArray(data.services) ? data.services : data.items ?? [];
        setServices(items);
        mergePassengersFromServices(items);
        setLoadingServices(false);

        const p = data.pagination;
        const apiPageSize = Number(p?.page_size) || requestedPageSize;
        const effectivePageSize = Math.min(
          MAX_PAGE_SIZE,
          pageSizeParam !== undefined
            ? pageSizeParam
            : apiPageSize || paginationRef.current.pageSize || 50
        );

        if (
          pageSizeParam !== undefined &&
          apiPageSize > 0 &&
          apiPageSize < pageSizeParam &&
          items.length < pageSizeParam
        ) {
          showToast(
            `El servidor devolvió ${items.length} de ${pageSizeParam} solicitados (límite ${apiPageSize}). Reinicia el backend si acabas de actualizar.`,
            "info"
          );
        }

        const totalCount = p?.total_count ?? items.length;
        const totalPages =
          totalCount > 0
            ? Math.ceil(totalCount / Math.max(effectivePageSize, 1))
            : 1;
        const currentPage = p?.page ?? page;

        const nextPagination = {
          page: currentPage,
          totalPages,
          totalCount,
          pageSize: effectivePageSize,
          hasNext: currentPage < totalPages,
          hasPrevious: currentPage > 1,
        };
        paginationRef.current = nextPagination;
        setPagination(nextPagination);
      },
      onError: () => {
        setLoadingServices(false);
        showToast('Error cargando servicios', 'error');
      },
    }, ctrl.signal);
  }, [list, mergePassengersFromServices]);

  const getFiltersDebounceMs = useCallback((currentFilters: typeof filters) => {
    if (currentFilters.id?.trim()) return 500;
    if (
      currentFilters.fromDate ||
      currentFilters.toDate ||
      currentFilters.fromTime ||
      currentFilters.toTime
    ) {
      return 400;
    }
    return 0;
  }, []);

  // Cargar servicios cuando cambien los filtros (debounce en ID y fecha/hora)
  useEffect(() => {
    const delay = getFiltersDebounceMs(filters);
    const timeoutId = setTimeout(() => {
      loadServices(1, filters);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      activeRequest.current?.abort();
      setLoadingServices(false);
    };
  }, [filters, loadServices, getFiltersDebounceMs]);

  useEffect(() => {
    loadFilterPassengerOptionsRef.current = loadFilterPassengerOptions;
  }, [loadFilterPassengerOptions]);

  // Cargar opciones del filtro de usuarios una sola vez al montar la página
  useEffect(() => {
    filterPassengersRequestRef.current?.abort();
    const ctrl = new AbortController();
    filterPassengersRequestRef.current = ctrl;

    loadFilterPassengerOptionsRef.current(
      {},
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? resp ?? {};
          const items = Array.isArray(data.passengers) ? data.passengers : [];
          if (items.length === 0) return;

          setFilterPassengers((prev) => {
            const byId = new Map(prev.map((p) => [p.id, p]));
            for (const p of items) {
              const id = Number(p.id);
              if (!id || Number.isNaN(id)) continue;
              byId.set(id, {
                id,
                label: formatPassengerFilterLabel(p),
                company_id: p.company_id ?? p.company ?? null,
              });
            }
            return Array.from(byId.values()).sort((a, b) =>
              a.label.localeCompare(b.label, "es", { sensitivity: "base" })
            );
          });
        },
        onError: () => {
          if (ctrl.signal.aborted) return;
          showToast("Error cargando pasajeros para filtros", "error");
        },
      },
      ctrl.signal
    );

    return () => {
      ctrl.abort();
      if (filterPassengersRequestRef.current === ctrl) {
        filterPassengersRequestRef.current = null;
      }
    };
  }, []);

  // ✅ cargar empresas cuando se abra el formulario
  const loadCompanies = useCallback(() => {
    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    listCompanies({}, {
      onSuccess: (resp: any) => {
        const items =  resp?.data.companies ?? [];
        const mapped = items.map((c: any) => ({
          value: c.id,
          label: c.name ?? c.company_name ?? 'Sin nombre',
        }));
        setCompanies(mapped);
      },
      onError: () => showToast('Error cargando empresas', 'error'),
    }, ctrl.signal);
  }, [listCompanies]);
  useEffect(() => {
    if (showForm && step === 1) loadCompanies();
  }, [showForm, step, loadCompanies]);

  useEffect(() => {
    const ctrl = new AbortController();
    listCompanies({}, {
      onSuccess: (resp: any) => {
        const items = resp?.data.companies ?? [];
        const mapped = items.map((c: any) => ({
          value: c.id,
          label: c.name ?? c.company_name ?? 'Sin nombre',
        }));
        setCompanies(mapped);
      },
    }, ctrl.signal);
    return () => ctrl.abort();
  }, [listCompanies]);

  // Paso 2: un solo GET (services/create/step2/:company_id/)
  useEffect(() => {
    if (!showForm || step !== 2 || !form.company) return;

    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    step2(form.company, {
      onSuccess: (resp: any) => {
        const d = resp?.data ?? {};
        setPassengers(Array.isArray(d.passengers) ? d.passengers : []);
        const items = d.locations ?? [];
        setLocations(
          items.map((c: any) => ({
            value: String(c.id),
            label: c.name ?? "Sin nombre",
          }))
        );
      },
      onError: () => showToast("Error cargando pasajeros y ubicaciones", "error"),
    }, ctrl.signal);

    return () => ctrl.abort();
  }, [showForm, step, form.company, step2]);

  // Paso 3: un solo GET (services/create/step3/:company_id/)
  useEffect(() => {
    if (!showForm || step !== 3 || !form.company) return;

    const ctrl = new AbortController();
    activeRequest.current = ctrl;

    step3(form.company, {
      onSuccess: (resp: any) => {
        const d = resp?.data ?? {};
        setDrivers(Array.isArray(d.drivers) ? d.drivers : []);
        setAuthorizers(Array.isArray(d.authorizers) ? d.authorizers : []);
        setRequesters(Array.isArray(d.requesters) ? d.requesters : []);
        setCostCenters(Array.isArray(d.cost_centers) ? d.cost_centers : []);
      },
      onError: () => showToast("Error cargando asignaciones", "error"),
    }, ctrl.signal);

    return () => ctrl.abort();
  }, [showForm, step, form.company, step3]);

  // ✅ funciones de stepper
  const nextStep = () => setStep((s) => Math.min(3, s + 1));
  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  // ✅ función para guardar servicio
  const callbackSave = async (): Promise<any> => {
      clearErrors();
      const loadingStartedAt = Date.now();

      const endLoading = async () => {
        await waitMinServiceCreationLoading(loadingStartedAt);
        setIsSubmitting(false);
        setSavingLocked(false);
      };

      try {
        if (!isServiceDateValid(form.service_date)) {
          showToast(
            `La fecha del servicio no es válida. Debe ser ${getMinServiceDateYmd()} o posterior.`,
            "error"
          );
          setStep(1);
          await endLoading();
          return;
        }

        if (isServiceDateTimeInPast(form.service_date, form.service_time)) {
          showToast(
            "La fecha y hora del servicio no puede ser anterior a la hora actual.",
            "error"
          );
          setStep(1);
          await endLoading();
          return;
        }

        const resp = await store(form);

        const httpStatus = resp?.http_status ?? resp?.status;
        const httpFailed = typeof httpStatus === 'number' && httpStatus >= 400;
        const apiFailed = resp?.success === false;
        const hasErrors =
          resp?.errors != null &&
          (Array.isArray(resp.errors)
            ? resp.errors.length > 0
            : typeof resp.errors === 'object' && Object.keys(resp.errors).length > 0);

        const pickFirstMessage = (r: typeof resp): string => {
          if (typeof r?.message === 'string' && r.message.trim()) return r.message.trim();
          if (typeof r?.detail === 'string') return r.detail;
          const fromErrors =
            r?.errors?.detail ??
            r?.data?.detail ??
            Object.values(r?.errors ?? {})[0] ??
            Object.values(r?.data ?? {})[0];
          if (fromErrors == null) return '';
          return Array.isArray(fromErrors) ? String(fromErrors[0] ?? '') : String(fromErrors);
        };

        if (!resp || httpFailed || apiFailed || hasErrors) {
          setErrorsFromResponse(resp);

          const firstError =
            (resp?.http_status === 403 || resp?.is_permission_denied
              ? resp?.message ||
                'No tienes permisos para crear servicios. Contacta a un administrador.'
              : pickFirstMessage(resp)) ||
            'No se pudo crear el servicio. Revisa los datos o intenta de nuevo.';

          showToast(firstError, 'error');
          await endLoading();
          return;
        }

        // ✅ Éxito: guardar datos El Abra y redirigir al servicio creado
        const createdId = resp?.data?.id ?? resp?.data?.service?.id ?? resp?.id;

        if (
          createdId != null &&
          isAdicionalEnabledForCompany(companyName) &&
          (form.management || form.superintendency || form.area || form.service_reason_text?.trim())
        ) {
          try {
            await updateAdicional(Number(createdId), {
              management: form.management ?? null,
              superintendency: form.superintendency ?? null,
              area: form.area ?? null,
              service_reason_text: form.service_reason_text?.trim() || null,
            });
          } catch (adicionalErr) {
            console.error('Error guardando datos El Abra:', adicionalErr);
            showToast(
              'Servicio creado, pero no se pudieron guardar los datos El Abra. Complétalos en edición.',
              'error'
            );
          }
        }

        await waitMinServiceCreationLoading(loadingStartedAt);

        showToast(resp?.message ?? 'Servicio creado correctamente', 'success');
        setIsSubmitting(false);
        setSavingLocked(false);
        setShowForm(false);
        setStep(1);
        if (createdId != null) {
          navigate(`/services/edit/${createdId}`);
        } else {
          loadServices();
        }
      } catch (err: any) {
        // ⚙️ Captura errores inesperados del servidor
        const resp = err?.response?.data ?? err;
        setErrorsFromResponse(resp);

        const firstError =
          Object.values(resp?.detail ?? {})[0] ??
          Object.values(resp?.data ?? {})[0] ??
          'Error creando servicio';
        showToast(
          Array.isArray(firstError) ? firstError[0] : firstError,
          'error'
        );

        console.error('❌ Error al crear servicio:', err);
        await endLoading();
      }
    };

  useEffect(() => {
    const unsubscribe = subscribeSocket((data) => {
      console.log("📡 Evento recibido del WebSocket:", data);

      // Si el backend envía una notificación de actualización de servicio
      if (data?.type === "service_update" && data?.payload && data?.payload?.id) {
        const updatedService = data.payload;
        console.log("✅ Notificación de servicio recibida:", updatedService);
        console.log("🔄 Recargando lista de servicios para mantener sincronización...");

        const currentPage = paginationRef.current.page;
        loadServices(
          currentPage,
          filtersRef.current,
          paginationRef.current.pageSize
        );

        setServices((prev) => {
          const exists = prev.some((s) => s.id === updatedService.id);
          if (exists) {
            return prev.map((s) => (s.id === updatedService.id ? updatedService : s));
          }
          return [updatedService, ...prev];
        });
      }
    });

    return () => {
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers estables (useCallback) para no invalidar la memoización de las filas
  const handleEdit = useCallback(
    (item: Service): void => {
      navigate(`/services/edit/${item.id}`); // Ajusta la ruta si es diferente
    },
    [navigate]
  );

  const handleDuplicate = useCallback((item: Service) => setDuplicateTarget(item), []);
  const handleRetorno = useCallback((item: Service) => setRetornoTarget(item), []);
  const handleEditRoute = useCallback((item: Service) => setRouteEditTarget(item), []);
  const handleDelete = useCallback((id: number): void => {
    throw new Error('Function not implemented.');
  }, []);
  const handleFiltersChange = useCallback((newFilters: ServicesTableFilters) => {
    setFilters((prev) => {
      const next = mapTableFiltersToPageFilters(newFilters);
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, []);

  const statusTransitionLoadingIdsRef = useRef<number[]>([]);
  useEffect(() => {
    statusTransitionLoadingIdsRef.current = statusTransitionLoadingIds;
  }, [statusTransitionLoadingIds]);

  const assignDriverLoadingIdsRef = useRef<number[]>([]);
  useEffect(() => {
    assignDriverLoadingIdsRef.current = assignDriverLoadingIds;
  }, [assignDriverLoadingIds]);

  const releaseDriverLoadingIdsRef = useRef<number[]>([]);
  useEffect(() => {
    releaseDriverLoadingIdsRef.current = releaseDriverLoadingIds;
  }, [releaseDriverLoadingIds]);

  const driversLoadingCompanyIdsRef = useRef<number[]>([]);
  useEffect(() => {
    driversLoadingCompanyIdsRef.current = driversLoadingCompanyIds;
  }, [driversLoadingCompanyIds]);

  const handleLoadDriversForCompany = useCallback(
    async (companyId: number) => {
      if (!companyId || driversLoadingCompanyIdsRef.current.includes(companyId)) return;

      setDriversLoadingCompanyIds((prev) => [...prev, companyId]);
      try {
        const resp: any = await step3(companyId);
        const raw = resp?.data?.drivers ?? resp?.drivers ?? [];
        const options = (Array.isArray(raw) ? raw : [])
          .map((d: any) => ({
            value: Number(d?.value ?? d?.id),
            label: String(d?.label ?? d?.name ?? "").trim(),
            is_available: d?.is_available,
          }))
          .filter((d: { value: number; label: string }) => Number.isFinite(d.value) && d.value > 0);

        setDriverOptionsByCompany((prev) => ({ ...prev, [companyId]: options }));
      } catch {
        showToast("Error cargando conductores", "error");
        setDriverOptionsByCompany((prev) => ({ ...prev, [companyId]: [] }));
      } finally {
        setDriversLoadingCompanyIds((prev) => prev.filter((id) => id !== companyId));
      }
    },
    [step3]
  );

  const handleAssignDriver = useCallback(
    async (item: Service, driverId: number) => {
      if (!item.id) return;
      const serviceId = Number(item.id);
      if (assignDriverLoadingIdsRef.current.includes(serviceId)) return;

      setAssignDriverLoadingIds((prev) => [...prev, serviceId]);
      try {
        const resp: any = await assignDriver(serviceId, driverId);
        const getErrorMessage = (payload: any): string => {
          if (!payload) return "No se pudo asignar el conductor.";
          const errors = payload?.errors;
          if (typeof errors === "string" && errors.trim()) return errors.trim();
          if (Array.isArray(errors) && errors.length > 0) return String(errors[0]);
          if (errors && typeof errors === "object") {
            const firstValue = Object.values(errors)[0];
            if (Array.isArray(firstValue) && firstValue.length > 0) return String(firstValue[0]);
            if (firstValue != null) return String(firstValue);
          }
          if (typeof payload?.message === "string" && payload.message.trim()) {
            return payload.message.trim();
          }
          if (typeof payload?.detail === "string" && payload.detail.trim()) {
            return payload.detail.trim();
          }
          return "No se pudo asignar el conductor.";
        };
        const failed =
          !resp ||
          resp?.success === false ||
          (typeof resp?.http_status === "number" && resp.http_status >= 400) ||
          (resp?.errors && Object.keys(resp.errors).length > 0);

        if (failed) {
          showToast(getErrorMessage(resp), "error");
          return;
        }

        showToast(resp?.message || "Conductor asignado correctamente", "success");
        loadServices(paginationRef.current.page, filtersRef.current, paginationRef.current.pageSize);
      } catch (error: any) {
        const errorData = error?.response?.data;
        const errors = errorData?.errors;
        const msg =
          (typeof errors === "string" && errors) ||
          (Array.isArray(errors) && errors.length > 0 ? String(errors[0]) : "") ||
          (errors && typeof errors === "object"
            ? Array.isArray(Object.values(errors)[0])
              ? String((Object.values(errors)[0] as any[])[0] ?? "")
              : String(Object.values(errors)[0] ?? "")
            : "") ||
          errorData?.message ||
          errorData?.detail ||
          error?.message ||
          "Error al asignar conductor";
        showToast(String(msg), "error");
      } finally {
        setAssignDriverLoadingIds((prev) => prev.filter((id) => id !== serviceId));
      }
    },
    [assignDriver, loadServices]
  );

  const handleRequestReleaseDriver = useCallback((item: Service) => {
    if (!item.id) return;
    if (String(item.status || "") !== ServiceStatus.ASIGNADO) return;
    setReleaseDriverTarget(item);
  }, []);

  const handleConfirmReleaseDriver = useCallback(
    async (comment: string) => {
      const item = releaseDriverTarget;
      if (!item?.id) return;
      const serviceId = Number(item.id);
      if (releaseDriverLoadingIdsRef.current.includes(serviceId)) return;

      setReleaseDriverLoadingIds((prev) => [...prev, serviceId]);
      try {
        const resp: any = await releaseDriver(serviceId, comment);
        const getErrorMessage = (payload: any): string => {
          if (!payload) return "No se pudo liberar el conductor.";
          const errors = payload?.errors;
          if (typeof errors === "string" && errors.trim()) return errors.trim();
          if (Array.isArray(errors) && errors.length > 0) return String(errors[0]);
          if (errors && typeof errors === "object") {
            const firstValue = Object.values(errors)[0];
            if (Array.isArray(firstValue) && firstValue.length > 0) return String(firstValue[0]);
            if (firstValue != null) return String(firstValue);
          }
          if (typeof payload?.message === "string" && payload.message.trim()) {
            return payload.message.trim();
          }
          if (typeof payload?.detail === "string" && payload.detail.trim()) {
            return payload.detail.trim();
          }
          return "No se pudo liberar el conductor.";
        };
        const failed =
          !resp ||
          resp?.success === false ||
          (typeof resp?.http_status === "number" && resp.http_status >= 400) ||
          (resp?.errors && Object.keys(resp.errors).length > 0);

        if (failed) {
          showToast(getErrorMessage(resp), "error");
          return;
        }

        showToast(resp?.message || "Conductor liberado correctamente", "success");
        setReleaseDriverTarget(null);
        loadServices(paginationRef.current.page, filtersRef.current, paginationRef.current.pageSize);
      } catch (error: any) {
        const errorData = error?.response?.data;
        const errors = errorData?.errors;
        const msg =
          (typeof errors === "string" && errors) ||
          (Array.isArray(errors) && errors.length > 0 ? String(errors[0]) : "") ||
          (errors && typeof errors === "object"
            ? Array.isArray(Object.values(errors)[0])
              ? String((Object.values(errors)[0] as any[])[0] ?? "")
              : String(Object.values(errors)[0] ?? "")
            : "") ||
          errorData?.message ||
          errorData?.detail ||
          error?.message ||
          "Error al liberar conductor";
        showToast(String(msg), "error");
      } finally {
        setReleaseDriverLoadingIds((prev) => prev.filter((id) => id !== serviceId));
      }
    },
    [releaseDriver, loadServices, releaseDriverTarget]
  );

  const handleStatusTransition = useCallback(
    async (item: Service, nextStatus: ServiceStatus) => {
      if (!item.id) return;
      const serviceId = Number(item.id);
      if (statusTransitionLoadingIdsRef.current.includes(serviceId)) return;

      if (
        nextStatus === ServiceStatus.AUTORIZADO &&
        !item.authorizer &&
        !String(item.authorizer_name ?? "").trim()
      ) {
        showToast(
          "Debe asignarse un autorizador antes de autorizar el servicio.",
          "error"
        );
        return;
      }

      setStatusTransitionLoadingIds((prev) => [...prev, serviceId]);
      try {
        const resp: any = await updateStatus(serviceId, nextStatus);
        const getErrorMessage = (payload: any): string => {
          if (!payload) return "No se pudo cambiar el estado del servicio.";
          const errors = payload?.errors;
          if (typeof errors === "string" && errors.trim()) return errors.trim();
          if (Array.isArray(errors) && errors.length > 0) return String(errors[0]);
          if (errors && typeof errors === "object") {
            const firstValue = Object.values(errors)[0];
            if (Array.isArray(firstValue) && firstValue.length > 0) return String(firstValue[0]);
            if (firstValue != null) return String(firstValue);
          }
          if (typeof payload?.message === "string" && payload.message.trim()) {
            return payload.message.trim();
          }
          if (typeof payload?.detail === "string" && payload.detail.trim()) {
            return payload.detail.trim();
          }
          return "No se pudo cambiar el estado del servicio.";
        };
        const failed =
          !resp ||
          resp?.success === false ||
          (typeof resp?.http_status === "number" && resp.http_status >= 400) ||
          (resp?.errors && Object.keys(resp.errors).length > 0);

        if (failed) {
          showToast(getErrorMessage(resp), "error");
          return;
        }

        showToast(resp?.message || "Estado actualizado correctamente", "success");
        loadServices(paginationRef.current.page, filtersRef.current, paginationRef.current.pageSize);
      } catch (error: any) {
        const errorData = error?.response?.data;
        const errors = errorData?.errors;
        const msg =
          (typeof errors === "string" && errors) ||
          (Array.isArray(errors) && errors.length > 0 ? String(errors[0]) : "") ||
          (errors && typeof errors === "object"
            ? Array.isArray(Object.values(errors)[0])
              ? String((Object.values(errors)[0] as any[])[0] ?? "")
              : String(Object.values(errors)[0] ?? "")
            : "") ||
          errorData?.message ||
          errorData?.detail ||
          error?.message ||
          "Error al cambiar estado del servicio";
        showToast(String(msg), "error");
      } finally {
        setStatusTransitionLoadingIds((prev) => prev.filter((id) => id !== serviceId));
      }
    },
    [loadServices, updateStatus]
  );
  useEffect(() => {
    setIsStepValid(isStepValidByNumber(step, form, companyName));
  }, [form, step, companyName]);
  return (
    <div className="services-page">
      <header className="services-header">
        <div className="services-header-text">
          <h1>Servicios</h1>
        </div>
        <div className="services-header-actions">
          {canExportPlanilla && (
            <button
              type="button"
              className="btn-export-planilla"
              onClick={() => setShowPlanillaExport(true)}
            >
              Descargar Excel
            </button>
          )}
          {canImportElAbraPlanilla && (
            <button
              type="button"
              className="btn-export-planilla"
              onClick={() => setShowElAbraImport(true)}
            >
              Importar planilla El Abra
            </button>
          )}
          <button
            className="btn-create"
            onClick={() => {
              setForm(createEmptyServiceForm());
              setStep(1);
              setIsSubmitting(false);
              clearErrors();
              setShowForm(true);
            }}
          >
            + Nuevo Servicio
          </button>
        </div>
      </header>

      <section className="services-list">
        <ServicesTable
          data={services}
          totalCount={pagination.totalCount}
          loading={fetching.list || loadingServices}
          passengerOptions={passengerFilterOptions}
          companyOptions={companies}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          onEditRoute={handleEditRoute}
          onRetorno={handleRetorno}
          onStatusTransition={handleStatusTransition}
          statusTransitionLoadingIds={statusTransitionLoadingIds}
          onAssignDriver={handleAssignDriver}
          assignDriverLoadingIds={assignDriverLoadingIds}
          onReleaseDriver={handleRequestReleaseDriver}
          releaseDriverLoadingIds={releaseDriverLoadingIds}
          driverOptionsByCompany={driverOptionsByCompany}
          onLoadDriversForCompany={handleLoadDriversForCompany}
          driversLoadingCompanyIds={driversLoadingCompanyIds}
          onDelete={handleDelete}
          onFiltersChange={handleFiltersChange}
        />
        {pagination.totalCount > 0 && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            onPageChange={(page) => {
              loadServices(page, filtersRef.current);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onPageSizeChange={(newSize) => {
              loadServices(1, filtersRef.current, newSize);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        )}
      </section>

      {showForm && (
        <ServiceCreationWizard
          step={step}
          form={form}
          isSubmitting={isSubmitting}
          savingLocked={savingLocked}
          isStepValid={isStepValid}
          companies={companies}
          passengers={passengers}
          locations={locations}
          drivers={drivers}
          authorizers={authorizers}
          requesters={requesters}
          costCenters={costCenters}
          fieldErrors={fieldErrors}
          onFormChange={(field, value) =>
            setForm((prev) => ({ ...prev, [field]: value }))
          }
          onFormPatch={(patch) =>
            setForm((prev) => ({ ...prev, ...patch }))
          }
          onSelectedPassengerChange={() => undefined}
          onAddSequence={(seq) => setForm((prev) => ({ ...prev, sequences: seq }))}
          onPassengersCreated={(created) => {
            setPassengers((prev) => [
              ...created.map((p) => ({
                id: p.id,
                name: [p.name, p.first_lastname, p.second_lastname]
                  .map((part) => String(part ?? "").trim())
                  .filter(Boolean)
                  .join(" "),
                addresses:
                  p.addresses?.length > 0
                    ? p.addresses
                    : p.street
                      ? [{ id: 0, street: p.street }]
                      : [],
              })),
              ...prev,
            ]);
          }}
          onPassengerUpdated={(updated) => {
            setPassengers((prev) =>
              prev.map((p) =>
                Number(p.id) === Number(updated.id)
                  ? {
                      ...p,
                      name: updated.name,
                      addresses:
                        updated.addresses.length > 0
                          ? updated.addresses
                          : p.addresses,
                    }
                  : p
              )
            );
          }}
          onStepChange={setStep}
          onNext={nextStep}
          onPrev={prevStep}
          onSave={callbackSave}
          onSubmitStart={() => {
            setSavingLocked(true);
            setIsSubmitting(true);
            clearErrors();
          }}
          onCancel={() => {
            setForm(createEmptyServiceForm());
            clearErrors();
            setIsSubmitting(false);
            setShowForm(false);
            setStep(1);
          }}
        />
      )}

      <ReleaseDriverModal
        isOpen={Boolean(releaseDriverTarget)}
        driverName={releaseDriverTarget?.driver_name}
        loading={Boolean(
          releaseDriverTarget?.id &&
            releaseDriverLoadingIds.includes(Number(releaseDriverTarget.id))
        )}
        onClose={() => {
          if (
            releaseDriverTarget?.id &&
            releaseDriverLoadingIds.includes(Number(releaseDriverTarget.id))
          ) {
            return;
          }
          setReleaseDriverTarget(null);
        }}
        onConfirm={handleConfirmReleaseDriver}
      />

      <ServiceRouteEditModal
        isOpen={Boolean(routeEditTarget)}
        service={routeEditTarget}
        onClose={() => setRouteEditTarget(null)}
        onSaved={() =>
          loadServices(
            paginationRef.current.page,
            filtersRef.current,
            paginationRef.current.pageSize
          )
        }
      />

      <ServiceDuplicateModal
        isOpen={Boolean(duplicateTarget)}
        service={duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onSuccess={(newServiceIds) => {
          if (newServiceIds.length === 1) {
            navigate(`/services/edit/${newServiceIds[0]}`);
          } else {
            loadServices(paginationRef.current.page, filtersRef.current);
          }
        }}
      />

      <ServiceReturnModal
        isOpen={Boolean(retornoTarget)}
        service={retornoTarget}
        onClose={() => setRetornoTarget(null)}
        onSuccess={(newServiceId) => {
          if (newServiceId) {
            navigate(`/services/edit/${newServiceId}`);
          } else {
            loadServices(paginationRef.current.page, filtersRef.current);
          }
        }}
      />

      {canExportPlanilla && (
        <ServicesPlanillaExportModal
          isOpen={showPlanillaExport}
          onClose={() => setShowPlanillaExport(false)}
        />
      )}

      {canImportElAbraPlanilla && (
        <ElAbraExcelImportModal
          isOpen={showElAbraImport}
          onClose={() => setShowElAbraImport(false)}
          onCreated={() => loadServices(paginationRef.current.page, filtersRef.current)}
        />
      )}
    </div>
  );
};

export default ServicesPage;
